/* Source: /home/sidsolaiyappan/skooch_ai/scheduler/scheduler_actions.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { computePlaceSchTaskBlock, computeMovePlacement, resolveAcceptedTaskSeed, computeAcceptedScheduleRewrite, computeAcceptedTasksList } from "../shared/task_handlers.js";
import { createTaskRemote, updateTaskStatusOnlyRemote } from "../shared/task_crud_remote.js";
import { upsertScheduleBlocks, deleteScheduleBlocksForTask } from "../shared/schedule_block_crud.js";
import { schedulerRequestFailed, schedulerErrorText, clientErrorBody } from "../shared/scheduler_request_helpers.js";
import { getAccessToken } from "../shared/auth_token.js";
import { get_supabase } from "../shared/supabase_config.js";
import { computeOrderedSchTasks, computeSuggestionEffect, schedulerTasksFromPending } from "../shared/scheduler_helpers.js";
import { runOptimize } from "./scheduler_optimize.js";
import { fixedEventId } from "../shared/fixed_event_helpers.js";
import { deleteFixedEventRemote } from "../shared/fixed_event_crud.js";
import { deleteTaskEverywhere } from "../shared/task_lifecycle.js";
import { logInteractionEvent } from "../shared/event_capture.js";
import { pushGcalAfterLocalChange } from "../shared/gcal_connect.js";
import { maybeAskAfterMove, maybeAskAfterReject } from "../shared/learning_handlers.js";
import { getStoreTasks, getStoreSchedule, getStoreSchedulerTasks, getStoreFixedBlocks, getStoreUserId, getStoreIsAnonymous, getStoreOverflow, getStoreSchPrefBuf, getStoreSchConstraints, getStoreSchPopupBlock, getStoreSchOverflowExpandedIds, getStoreSchOptimizeInputTotal, setStoreTasks, setStoreSchedule, setStoreSchedulerTasks, setStoreSchLog, setStoreSchDirty, setStoreSchPrefBuf, setStoreSchConstraints, setStoreFixedBlocks, setStoreSchPopupOpen, setStoreSchOverflowExpandedIds, setStoreSchPrefPeak, setStoreSchPrefBlock, setStoreSchPrefStrat, setStoreSchPrefAfter, setStoreLearningTick } from "../shared/store.js";
async function placeSchTask(taskId, day, startMins) {
  let _weekIso = (window.__skoochWeekIsoDays ? window.__skoochWeekIsoDays : []);
  let _tasks = getStoreTasks();
  let _schedule = getStoreSchedule();
  let _movPrior = null;
  for (const b of _schedule) {
    if ((b.task_id === taskId)) {
      _movPrior = b;
    }
  }
  let _fixedBlocks = getStoreFixedBlocks();
  let _r = computeMovePlacement(taskId, day, startMins, _weekIso, _tasks, _schedule, _fixedBlocks);
  if (!_r["ok"]) {
    if (_r["msg"]) {
      setStoreSchLog(_r["msg"]);
    } else {
      setStoreSchLog("No free slot there \u2014 that day's packed around then. Try an earlier time or a different day.");
    }
    return;
  }
  let _wasPlaced = _r["wasPlaced"];
  if (_wasPlaced) {
    let okDel = await deleteScheduleBlocksForTask(taskId, "Couldn't move task");
    if (!okDel) {
      return;
    }
  }
  let okSave = await upsertScheduleBlocks([_r["block"]], "Couldn't place task");
  if (!okSave) {
    return;
  }
  setStoreSchedule(_r["scheduleWithoutTask"].concat([_r["block"]]));
  setStoreSchDirty(true);
  let _mfs = null;
  if ((_movPrior && _movPrior.start_time)) {
    let _hm = String(_movPrior.start_time).split(":");
    if ((_hm.length >= 2)) {
      _mfs = ((Number(_hm[0]) * 60) + Number(_hm[1]));
    }
  }
  let _mfd = (_movPrior ? _movPrior.day : null);
  let _mconf = (_r["block"] ? _r["block"].confidence : null);
  let _mcat = (_r["block"] ? _r["block"].category : null);
  try {
    logInteractionEvent("placement_moved", {"entity_id": taskId, "category": _mcat, "slot_minutes": startMins, "day_of_week": day, "payload": {"from_slot": _mfs, "from_day": _mfd, "to_slot": startMins, "to_day": day, "confidence": _mconf}});
  } catch (__jac_e) {
    if (true) {
      let _ele = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  setStoreLearningTick(Date.now());
  let _mtitle = ((_r["block"] && _r["block"].task_title) ? _r["block"].task_title : "this task");
  try {
    maybeAskAfterMove(taskId, _mtitle, _mcat, _mfs, startMins);
  } catch (__jac_e) {
    if (true) {
      let _eml = __jac_e;
    } else {
      throw __jac_e;
    }
  }
}
async function acceptScheduledTask(taskId) {
  window.__skoochOpInFlight = true;
  let _tasks = getStoreTasks();
  let _schTasks = getStoreSchedulerTasks();
  let _schedule = getStoreSchedule();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _res = resolveAcceptedTaskSeed(taskId, _tasks, _schTasks, _schedule);
  let effectiveTaskId = taskId;
  let effectiveTask = _res["existing"];
  if (!effectiveTask) {
    let seedTask = _res["seed"];
    if (!seedTask) {
      setStoreSchLog("Couldn't accept task: task details are missing for this placement.");
      window.__skoochOpInFlight = false;
      return;
    }
    if (_uid) {
      let accessToken = await getAccessToken();
      let createdTask = await createTaskRemote(_uid, _isAnon, accessToken, seedTask.title, seedTask.category, seedTask.duration_mins, seedTask.priority, seedTask.deadline);
      if (schedulerRequestFailed(createdTask)) {
        setStoreSchLog(schedulerErrorText("Couldn't create task for accepted placement", createdTask));
        window.__skoochOpInFlight = false;
        return;
      }
      effectiveTaskId = createdTask.id;
      effectiveTask = createdTask;
    } else {
      effectiveTask = seedTask;
      setStoreTasks(_tasks.concat([effectiveTask]));
    }
  }
  let _rw = computeAcceptedScheduleRewrite(taskId, effectiveTaskId, effectiveTask, _schedule);
  let newSchedule = _rw["newSchedule"];
  let changed = _rw["changed"];
  let okSave = await upsertScheduleBlocks(changed, "Couldn't accept task");
  if (!okSave) {
    window.__skoochOpInFlight = false;
    return;
  }
  let updatedSchTasks = _schTasks.filter(st => {
    return (st.id !== taskId);
  });
  let acceptedTaskLocal = {"id": effectiveTaskId, "title": effectiveTask.title, "category": effectiveTask.category, "duration_mins": effectiveTask.duration_mins, "priority": effectiveTask.priority, "deadline": (effectiveTask.deadline ? effectiveTask.deadline : ""), "status": "accepted", "completed": false};
  if (_uid) {
    let accessToken = await getAccessToken();
    let _st = updateTaskStatusOnlyRemote(_uid, _isAnon, accessToken, effectiveTaskId, "accepted");
    _st = _st.catch(e => {
      return {"_sb_error": "client", "_sb_body": clientErrorBody(e)};
    });
    await _st;
    let _accTasks = [];
    if (window.__skoochServerless) {
      let _accTCols = "id,title,category,duration_mins,priority,deadline,status,completed,is_recurring,recurrence_interval,recurrence_unit,recurrence_weekday";
      let _accTResp = await get_supabase().from("tasks").select(_accTCols).eq("user_id", _uid).order("created_at", {"ascending": true}).order("id", {"ascending": true});
      _accTasks = ((_accTResp && _accTResp.data) ? _accTResp.data : []);
    } else {
      _accTasks = await window.__skoochJacCall("list_tasks", {"user_id": _uid, "access_token": accessToken});
    }
    setStoreTasks(_accTasks);
    setStoreTasks(computeAcceptedTasksList(_tasks, effectiveTaskId, acceptedTaskLocal));
  } else {
    setStoreTasks(_tasks.map(tt => {
      if ((tt.id === effectiveTaskId)) {
        tt["status"] = "accepted";
      }
      return tt;
    }));
  }
  setStoreSchedule(newSchedule);
  setStoreSchedulerTasks(updatedSchTasks);
  let syncedDevSnapshot = await window.__skoochPersistCurrentDevSnapshot("Accepted task saved.", updatedSchTasks, newSchedule);
  setStoreSchDirty(!syncedDevSnapshot);
  try {
    pushGcalAfterLocalChange();
  } catch (__jac_e) {
    if (true) {
      let _epg = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  let _accBlock = null;
  for (const b of _schedule) {
    if ((b.task_id === taskId)) {
      _accBlock = b;
    }
  }
  let _aslot = null;
  if ((_accBlock && _accBlock.start_time)) {
    let _hm = String(_accBlock.start_time).split(":");
    if ((_hm.length >= 2)) {
      _aslot = ((Number(_hm[0]) * 60) + Number(_hm[1]));
    }
  }
  let _aday = (_accBlock ? _accBlock.day : null);
  let _aconf = (_accBlock ? _accBlock.confidence : null);
  let _acat = (effectiveTask ? effectiveTask.category : null);
  try {
    logInteractionEvent("placement_accepted", {"entity_id": effectiveTaskId, "category": _acat, "slot_minutes": _aslot, "day_of_week": _aday, "payload": {"slot": _aslot, "day": _aday, "confidence": _aconf}});
  } catch (__jac_e) {
    if (true) {
      let _ele = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  window.__skoochOpInFlight = false;
}
async function rejectScheduledTask(taskId) {
  window.__skoochOpInFlight = true;
  let _schedule = getStoreSchedule();
  let _tasks = getStoreTasks();
  let _schTasks = getStoreSchedulerTasks();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let rejectTitle = "";
  for (const b of _schedule) {
    if (((b.task_id === taskId) && b.task_title)) {
      rejectTitle = b.task_title;
    }
  }
  if (!rejectTitle) {
    for (const t of _tasks) {
      if (((t.id === taskId) && t.title)) {
        rejectTitle = t.title;
      }
    }
  }
  let rejectNote = window.prompt("Why are you rejecting this placement? You can leave a note like a preferred day or time.", "");
  if (((rejectNote === null) || (rejectNote === undefined))) {
    return;
  }
  let _rblock = null;
  for (const _rb of _schedule) {
    if ((_rb.task_id === taskId)) {
      _rblock = _rb;
    }
  }
  let _rslot = null;
  if ((_rblock && _rblock.start_time)) {
    let _rhm = String(_rblock.start_time).split(":");
    if ((_rhm.length >= 2)) {
      _rslot = ((Number(_rhm[0]) * 60) + Number(_rhm[1]));
    }
  }
  let _rdow = (_rblock ? _rblock.day : null);
  let _rcat = (_rblock ? _rblock.category : null);
  let _rconf = (_rblock ? _rblock.confidence : null);
  let _reason = (rejectNote ? rejectNote : null);
  let newSchedule = _schedule.filter(b => {
    return (b.task_id !== taskId);
  });
  let okSave = await deleteScheduleBlocksForTask(taskId, "Couldn't reject task");
  if (!okSave) {
    return;
  }
  setStoreSchedule(newSchedule);
  let syncedDevSnapshot = await window.__skoochPersistCurrentDevSnapshot("Rejected placement removed.", _schTasks, newSchedule);
  if (!syncedDevSnapshot) {
    setStoreSchDirty(true);
  }
  if (_uid) {
    let accessToken = await getAccessToken();
    let _st = updateTaskStatusOnlyRemote(_uid, _isAnon, accessToken, taskId, "pending");
    _st = _st.catch(e => {
      return {"_sb_error": "client", "_sb_body": clientErrorBody(e)};
    });
    await _st;
    let _rejTasks = [];
    if (window.__skoochServerless) {
      let _rejTCols = "id,title,category,duration_mins,priority,deadline,status,completed,is_recurring,recurrence_interval,recurrence_unit,recurrence_weekday";
      let _rejTResp = await get_supabase().from("tasks").select(_rejTCols).eq("user_id", _uid).order("created_at", {"ascending": true}).order("id", {"ascending": true});
      _rejTasks = ((_rejTResp && _rejTResp.data) ? _rejTResp.data : []);
    } else {
      _rejTasks = await window.__skoochJacCall("list_tasks", {"user_id": _uid, "access_token": accessToken});
    }
    setStoreTasks(_rejTasks);
    window.__skoochSyncSchTasksFromPending();
  } else {
    setStoreTasks(_tasks.map(tt => {
      if ((tt.id === taskId)) {
        tt["status"] = "pending";
      }
      return tt;
    }));
  }
  if ((rejectNote.trim().length > 0)) {
    setStoreSchLog(((("Rejected placement for " + (rejectTitle ? (("\"" + rejectTitle) + "\"") : "this task")) + ". Note: ") + rejectNote.trim()));
  } else {
    setStoreSchLog((("Placement rejected for " + (rejectTitle ? (("\"" + rejectTitle) + "\"") : "this task")) + "."));
  }
  try {
    logInteractionEvent("placement_rejected", {"entity_id": taskId, "category": _rcat, "slot_minutes": _rslot, "day_of_week": _rdow, "payload": {"rejected_slot": _rslot, "rejected_day": _rdow, "confidence": _rconf, "reason": _reason}});
  } catch (__jac_e) {
    if (true) {
      let _ele = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  setStoreLearningTick(Date.now());
  try {
    maybeAskAfterReject(taskId, (rejectTitle ? rejectTitle : "this task"), _rcat, _rslot);
  } catch (__jac_e) {
    if (true) {
      let _emr = __jac_e;
    } else {
      throw __jac_e;
    }
  }
  setStoreSchDirty(true);
  window.__skoochOpInFlight = false;
}
async function deleteTaskAndPlacements(taskId) {
  window.__skoochOpInFlight = true;
  let _schedule = getStoreSchedule();
  let newSchedule = _schedule.filter(b => {
    return (b.task_id !== taskId);
  });
  let okSave = await deleteScheduleBlocksForTask(taskId, "Couldn't remove placements");
  if (!okSave) {
    window.__skoochOpInFlight = false;
    return;
  }
  setStoreSchedule(newSchedule);
  let deletedOk = await window.__skoochDeleteTaskEverywhere(taskId, "Couldn't delete task");
  if (!deletedOk) {
    window.__skoochOpInFlight = false;
    return;
  }
  setStoreSchDirty(true);
  window.__skoochOpInFlight = false;
}
function orderedSchTasks() {
  return computeOrderedSchTasks(getStoreSchedulerTasks(), getStoreTasks(), getStoreOverflow());
}
function suggestionEffect(sg) {
  return computeSuggestionEffect(sg, getStoreSchPrefBuf(), getStoreSchedulerTasks(), getStoreSchConstraints());
}
function applySuggestion(sg, effectOverride = null) {
  let effect = (((effectOverride !== null) && (effectOverride !== undefined)) ? effectOverride : suggestionEffect(sg));
  setStoreSchPrefBuf(effect.nextBuffer);
  setStoreSchedulerTasks(effect.nextTasks);
  setStoreSchConstraints(effect.nextConstraints);
  if ((effect.branch !== "none")) {
    setStoreSchDirty(true);
  }
  return effect;
}
async function applySuggestionAndOptimize(sg) {
  let effect = applySuggestion(sg);
  await runOptimize(effect.nextTasks, effect.nextConstraints, effect.nextBuffer);
}
function closeSchPopup() {
  setStoreSchPopupOpen(false);
}
async function removeSchPopupBlock() {
  let _popup = getStoreSchPopupBlock();
  let _uid = getStoreUserId();
  let _isAnon = getStoreIsAnonymous();
  let _fixedBlocks = getStoreFixedBlocks();
  if (_popup.is_fixed) {
    let eid = fixedEventId(_popup);
    if (eid) {
      if (_uid) {
        let accessToken = await getAccessToken();
        let _dr = await deleteFixedEventRemote(_uid, _isAnon, accessToken, eid);
        if (!_dr["ok"]) {
          setStoreSchLog(_dr["errorMsg"]);
          return;
        }
      }
      setStoreFixedBlocks(_fixedBlocks.filter(b => {
        return (fixedEventId(b) !== eid);
      }));
    }
  } else {
    let tid = _popup.task_id;
    if (tid) {
      let deletedOk = await deleteTaskEverywhere(tid, "Couldn't delete task");
      if (!deletedOk) {
        return;
      }
    }
  }
  setStoreSchDirty(true);
  setStoreSchPopupOpen(false);
}
function setSchPref(field, val) {
  if ((field === "peak")) {
    setStoreSchPrefPeak(val);
  } else if ((field === "block")) {
    setStoreSchPrefBlock(parseInt(val));
  } else if ((field === "buf")) {
    setStoreSchPrefBuf(parseInt(val));
  } else if ((field === "strat")) {
    setStoreSchPrefStrat(val);
  } else if ((field === "after")) {
    setStoreSchPrefAfter(val);
  }
  setStoreSchDirty(true);
}
function toggleOverflowDetails(taskId) {
  let _ids = getStoreSchOverflowExpandedIds();
  if (_ids.includes(taskId)) {
    setStoreSchOverflowExpandedIds(_ids.filter(idv => {
      return (idv !== taskId);
    }));
  } else {
    setStoreSchOverflowExpandedIds(_ids.concat([taskId]));
  }
}
function schTasksSidebarCount() {
  let _schT = getStoreSchedulerTasks();
  let pending = getStoreTasks().filter(t => {
    return (t.status === "pending");
  });
  let fromP = schedulerTasksFromPending(pending);
  if ((_schT.length > 0)) {
    return _schT.length;
  }
  if ((fromP.length > 0)) {
    return fromP.length;
  }
  if ((getStoreSchOptimizeInputTotal() > 0)) {
    return getStoreSchOptimizeInputTotal();
  }
  return 0;
}
function removeSchConstraint(cid) {
  setStoreSchConstraints(getStoreSchConstraints().filter(c => {
    return (c.id !== cid);
  }));
  setStoreSchDirty(true);
}
export {acceptScheduledTask, applySuggestion, applySuggestionAndOptimize, closeSchPopup, deleteTaskAndPlacements, orderedSchTasks, placeSchTask, rejectScheduledTask, removeSchConstraint, removeSchPopupBlock, schTasksSidebarCount, setSchPref, suggestionEffect, toggleOverflowDetails};
if (typeof globalThis !== "undefined") { if (!globalThis.__jacEndpointEffects__) globalThis.__jacEndpointEffects__ = {}; Object.assign(globalThis.__jacEndpointEffects__, {"func:placeSchTask": {"is_writer": false, "touches": ["*"]}, "func:acceptScheduledTask": {"is_writer": true, "touches": ["*"]}, "func:rejectScheduledTask": {"is_writer": true, "touches": ["*"]}, "func:deleteTaskAndPlacements": {"is_writer": true, "touches": ["*"]}, "func:orderedSchTasks": {"is_writer": false, "touches": ["*"]}, "func:suggestionEffect": {"is_writer": false, "touches": ["*"]}, "func:applySuggestion": {"is_writer": false, "touches": ["*"]}, "func:applySuggestionAndOptimize": {"is_writer": false, "touches": ["*"]}, "func:closeSchPopup": {"is_writer": false, "touches": ["*"]}, "func:removeSchPopupBlock": {"is_writer": false, "touches": ["*"]}, "func:setSchPref": {"is_writer": false, "touches": ["*"]}, "func:toggleOverflowDetails": {"is_writer": false, "touches": ["*"]}, "func:schTasksSidebarCount": {"is_writer": false, "touches": ["*"]}, "func:removeSchConstraint": {"is_writer": false, "touches": ["*"]}}); };
//# sourceMappingURL=scheduler_actions.js.map
